import { Dispatch, useCallback, useMemo, useRef, useState } from 'react';

// https://github.com/bvaughn/react-window/blob/master/src/VariableSizeGrid.js See
// "findNearestItemBinarySearch". This is a binary search to find the index of the position to the
// left (or right) of an element at a given position. In other words, if we're scrolled to x, we
// need to know which element is the first to end after that position (that's our minimum).
const findNearestItem = (posIndexPairs: number[], pos: number, left = true) => {
  let high = posIndexPairs.length;
  let low = 0;
  while (low <= high) {
    const middle = low + Math.floor((high - low) / 2);
    const currentOffset = posIndexPairs[middle];
    if (currentOffset === pos) {
      return middle;
    }
    if (currentOffset < pos) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  if (low > 0) {
    return low - (left ? 1 : 0);
  }
  return 0;
};

interface Extrema {
  min: number;
  max: number;
}

interface VirtualInfo {
  visible: Extrema;
  sizes: number[];
  positions: number[];
}

export interface Virtual {
  rows: VirtualInfo;
  cols: VirtualInfo;
}

// Mostly stolen from https://github.com/bvaughn/react-window
const useVirtual = ({
  overscan,
  columnCount,
  columnWidth,
  width,
  rowCount,
  rowHeight,
  height
}: {
  overscan: number;
  columnCount: number;
  columnWidth: number | ((index: number) => number);
  width: number;
  rowCount: number;
  rowHeight: number | ((index: number) => number);
  height: number;
}): [Virtual, Dispatch<number>, Dispatch<number>] => {
  // We compute ahead of time the sizes and positions of all our elements. It's a big up-front cost,
  // but we can't risk having to do it on-the-fly as they're scrolling.
  const [colSizes, colPositions] = useMemo(() => {
    // First element is at position 0
    const positions = [0];
    const sizes = [typeof columnWidth === 'function' ? columnWidth(0) : columnWidth];

    let last = 0;
    for (let i = 1; i < columnCount; ++i) {
      // The position is the previous position plus the size of the last element.
      const point = last + sizes[i - 1];
      positions.push(point);
      last = point;
      sizes.push(typeof columnWidth === 'function' ? columnWidth(i) : columnWidth);
    }

    return [sizes, positions];
  }, [columnCount, columnWidth]);

  // Same as columns but rows
  const [rowSizes, rowPositions] = useMemo(() => {
    const positions = [0];
    const sizes = [typeof rowHeight === 'function' ? rowHeight(0) : rowHeight];

    let last = 0;
    for (let i = 1; i < rowCount; ++i) {
      const point = last + sizes[i - 1];
      positions.push(point);
      last = point;
      sizes.push(typeof rowHeight === 'function' ? rowHeight(i) : rowHeight);
    }

    return [sizes, positions];
  }, [rowCount, rowHeight]);

  const [left, setLeftRaw] = useState(0);
  const [top, setTopRaw] = useState(0);

  const { rows, cols } = useMemo(
    () => ({
      rows: {
        min: Math.max(0, findNearestItem(rowPositions, top, true) - overscan),
        max: Math.min(rowCount - 1, findNearestItem(rowPositions, top + height, false) + overscan)
      },
      cols: {
        min: Math.max(0, findNearestItem(colPositions, left, true) - overscan),
        max: Math.min(
          columnCount - 1,
          findNearestItem(colPositions, left + width, false) + overscan
        )
      }
    }),
    [rowPositions, colPositions, top, left, height, width, rowCount, columnCount, overscan]
  );

  // Side-channel for the scroll-time setters below; lets them skip setState
  // when a scroll event doesn't cross a virtualization boundary.
  const boundsRef = useRef(undefined as unknown as { rows: Extrema; cols: Extrema });
  // eslint-disable-next-line react-hooks/refs
  boundsRef.current = { rows, cols };

  const setLeft = useCallback<Dispatch<number>>(
    newLeft => {
      const newMin = Math.max(0, findNearestItem(colPositions, newLeft, true) - overscan);
      const newMax = Math.min(
        columnCount - 1,
        findNearestItem(colPositions, newLeft + width, false) + overscan
      );
      if (newMin !== boundsRef.current.cols.min || newMax !== boundsRef.current.cols.max) {
        setLeftRaw(newLeft);
      }
    },
    [colPositions, columnCount, overscan, width]
  );

  const setTop = useCallback<Dispatch<number>>(
    newTop => {
      const newMin = Math.max(0, findNearestItem(rowPositions, newTop, true) - overscan);
      const newMax = Math.min(
        rowCount - 1,
        findNearestItem(rowPositions, newTop + height, false) + overscan
      );
      if (newMin !== boundsRef.current.rows.min || newMax !== boundsRef.current.rows.max) {
        setTopRaw(newTop);
      }
    },
    [rowPositions, rowCount, overscan, height]
  );

  return [
    {
      rows: {
        visible: rows,
        sizes: rowSizes,
        positions: rowPositions
      },
      cols: {
        visible: cols,
        sizes: colSizes,
        positions: colPositions
      }
    },
    setLeft,
    setTop
  ];
};

export { useVirtual };
